import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";
import { Mail, Send, Users, GraduationCap } from "lucide-react";
import AttachmentsPanel from "@/components/AttachmentsPanel";

const SENDER = "trackly@nexusiqsolutions.com";

const TEMPLATES = {
  welcome_batch: {
    title: "Welcome — Training Batch",
    icon: Users,
    subject: (p) => `Welcome to ${p.training_title} — starting soon`,
    body: (p) => `Dear Participant,

Welcome aboard! You are enrolled in "${p.training_title}" delivered by NexusIQ Solutions.

Schedule: ${p.start_date || "TBD"} — ${p.end_date || "TBD"}
Topic: ${p.training_topic}
Batch Size: ${p.batch_size || "-"}

Access URLs, pre-reads and orientation details will follow shortly from the Program Management team.

Warm regards,
NexusIQ Program Management
${SENDER}`,
  },
  welcome_faculty: {
    title: "Welcome — Faculty",
    icon: GraduationCap,
    subject: (p) => `Faculty onboarding — ${p.training_title}`,
    body: (p) => `Dear Faculty,

Thank you for partnering with NexusIQ Solutions on "${p.training_title}".

Batch Size: ${p.batch_size || "-"}
Schedule: ${p.start_date || "TBD"} — ${p.end_date || "TBD"}

You will receive infrastructure access, curriculum outline and batch composition separately. Please reach out if anything is missing.

Warm regards,
NexusIQ Program Management
${SENDER}`,
  },
  feedback_training: {
    title: "Training Feedback",
    icon: Users,
    subject: (p) => `Your feedback on ${p.training_title}`,
    body: (p) => `Dear Participant,

The training "${p.training_title}" is now complete. We would greatly appreciate your feedback on the sessions, delivery and learning experience.

Please respond with your comments or share the feedback form.

Warm regards,
NexusIQ Program Management
${SENDER}`,
  },
  feedback_faculty: {
    title: "Faculty Feedback",
    icon: GraduationCap,
    subject: (p) => `Feedback on the Faculty — ${p.training_title}`,
    body: (p) => `Dear Participant,

Please share your feedback on the faculty who conducted "${p.training_title}". Your inputs help us continually improve delivery quality.

Warm regards,
NexusIQ Program Management
${SENDER}`,
  },
};

export default function EmailCompose({ program, stepNumber, kinds, canSend, onSent }) {
  return (
    <div className="grid gap-4">
      {kinds.map((kind) => (
        <SingleCompose key={kind} program={program} stepNumber={stepNumber} kind={kind} canSend={canSend} onSent={onSent} />
      ))}
    </div>
  );
}

function SingleCompose({ program, stepNumber, kind, canSend, onSent }) {
  const t = TEMPLATES[kind];
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const Icon = t.icon;

  useEffect(() => {
    setSubject(t.subject(program));
    setBody(t.body(program));
    // Pre-fill recipients from step form_data if available
    const step = program.steps.find((s) => s.step_number === stepNumber);
    const fd = step?.form_data || {};
    const key = kind === "welcome_batch" ? "batch_recipients"
      : kind === "welcome_faculty" ? "faculty_recipients"
      : null;
    if (key && fd[key]) setRecipients(fd[key]);
  }, [program, kind, stepNumber, t]);

  const parseRecipients = () => recipients.split(/[,;\s]+/).filter(Boolean);

  const send = async () => {
    const recs = parseRecipients();
    if (recs.length === 0) return toast.error("Add at least one recipient email");
    setLoading(true);
    try {
      // Program team is auto-cc'd
      const pmEmails = ["pm@nexusiqsolutions.com"]; // display-only; server actually broadcasts notifications too
      await api.post(`/programs/${program.id}/emails`, {
        kind, recipients: recs, cc: pmEmails, subject, body, attachment_ids: selectedIds,
      });
      toast.success(`Sent from ${SENDER} to ${recs.length} recipient(s)${selectedIds.length ? ` with ${selectedIds.length} attachment(s)` : ""}`);
      onSent && onSent();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send");
    } finally { setLoading(false); }
  };

  return (
    <Card className="border-border" data-testid={`compose-${kind}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-md bg-primary/10 text-primary grid place-items-center"><Icon className="w-4 h-4" /></div>
          <div>
            <div className="font-heading font-semibold text-sm">{t.title}</div>
            <div className="text-[11px] text-muted-foreground">From: <span className="font-mono">{SENDER}</span></div>
          </div>
        </div>
        <div className="grid gap-3">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Recipients (comma or space separated)</Label>
            <Textarea data-testid={`compose-recipients-${kind}`} rows={2} value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="alice@client.com, bob@client.com" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Subject</Label>
            <Input data-testid={`compose-subject-${kind}`} value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Body</Label>
            <Textarea data-testid={`compose-body-${kind}`} rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Attachments</Label>
            <AttachmentsPanel
              programId={program.id}
              stepNumber={stepNumber}
              canEdit={canSend}
              selectable={true}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            {canSend && (
              <Button size="sm" onClick={send} disabled={loading} data-testid={`compose-send-${kind}`}>
                <Send className="w-4 h-4 mr-2" /> {loading ? "Sending…" : `Send${selectedIds.length ? ` (${selectedIds.length} attached)` : ""}`}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function BillingCompose({ program, stepNumber, canSend, onSent }) {
  const [subject, setSubject] = useState(`Infrastructure Billing — ${program.training_title}`);
  const [body, setBody] = useState(`Dear Business Development Team,

Please find attached the infrastructure billing details for the training "${program.training_title}" (Client: ${program.client_name}).

Kindly share with the client for PO processing.

Regards,
NexusIQ IT Team
${SENDER}`);
  const [extraRecipients, setExtraRecipients] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (selectedIds.length === 0) {
      const ok = window.confirm("No files selected. Send billing email without attachments?");
      if (!ok) return;
    }
    setLoading(true);
    try {
      const recs = extraRecipients.split(/[,;\s]+/).filter(Boolean);
      await api.post(`/programs/${program.id}/billing/broadcast`, {
        subject, body,
        recipients: recs,
        attachment_ids: selectedIds,
      });
      toast.success(`Billing email sent from ${SENDER} to BD (cc PM, Finance)`);
      onSent && onSent();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send billing");
    } finally { setLoading(false); }
  };

  return (
    <Card className="border-border" data-testid="billing-compose">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-emerald-100 text-emerald-700 grid place-items-center"><Mail className="w-4 h-4" /></div>
          <div>
            <div className="font-heading font-semibold text-sm">Infrastructure Billing — Broadcast</div>
            <div className="text-[11px] text-muted-foreground">From: <span className="font-mono">{SENDER}</span> · To: Business Development · CC: Program Management, Finance</div>
          </div>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Attach files (upload/select screenshots, Excel, PDF)</Label>
          <div className="mt-2">
            <AttachmentsPanel
              programId={program.id}
              stepNumber={stepNumber}
              canEdit={canSend}
              selectable={true}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              refreshTick={refreshTick}
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Additional recipients (optional, e.g., specific BD contact)</Label>
          <Textarea data-testid="billing-extra-recipients" rows={2} value={extraRecipients} onChange={(e) => setExtraRecipients(e.target.value)} placeholder="bd.lead@nexusiqsolutions.com" />
        </div>

        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Subject</Label>
          <Input data-testid="billing-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Body</Label>
          <Textarea data-testid="billing-body" rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        {canSend && (
          <div className="flex items-center justify-end">
            <Button onClick={send} disabled={loading} data-testid="billing-send">
              <Send className="w-4 h-4 mr-2" /> {loading ? "Sending…" : `Send Billing (${selectedIds.length} attached)`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
